import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import FullScreenWorkoutEditor from '../workout/FullScreenWorkoutEditor';
import WorkoutViewModal from '../workout/WorkoutViewModal';
import Modal from '../ui/Modal';
import { getExerciseAnimationUrl, getExerciseAnimationWithFallback } from '../../utils/exerciseImages';
import { searchExercises } from '../../utils/exerciseSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category color mapping (same as Templates)
const CATEGORY_COLORS = {
  'chest': { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-400/50', label: 'Chest' },
  'back': { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-400/50', label: 'Back' },
  'legs': { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-400/50', label: 'Legs' },
  'shoulders': { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-400/50', label: 'Shoulders' },
  'arms': { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-400/50', label: 'Arms' },
  'core': { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-400/50', label: 'Core' },
  'cardio': { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-400/50', label: 'Cardio' },
  'full-body': { bg: 'bg-gradient-to-r from-purple-500/20 to-blue-500/20', text: 'text-purple-200', border: 'border-purple-400/50', label: 'Full Body' },
  'stretching': { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-400/50', label: 'Stretching' },
  'plyometrics': { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-400/50', label: 'Plyometrics' },
  'neck': { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-400/50', label: 'Neck' }
};

const getCategoryColor = (category) => {
  const normalized = category?.toLowerCase();
  return CATEGORY_COLORS[normalized] || { bg: 'bg-gray-500/20', text: 'text-gray-300', border: 'border-gray-400/50', label: category || '' };
};

// Helper function to adjust time by minutes
const adjustTime = (timeStr, minutes) => {
  if (!timeStr) return '09:00';
  const [hours, mins] = timeStr.split(':').map(Number);
  const totalMins = hours * 60 + mins + minutes;
  const newHours = Math.floor(((totalMins % 1440) + 1440) % 1440 / 60);
  const newMins = ((totalMins % 60) + 60) % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
};

// Time picker with increment/decrement buttons
const TimePicker = ({ value, onChange, label, disabled }) => {
  return (
    <div>
      <label className="block text-blue-200 text-sm font-medium mb-2">{label}</label>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(adjustTime(value, -30))}
          disabled={disabled}
          className="px-2 py-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 rounded-l-lg transition disabled:opacity-50"
          title="-30 min"
        >
          ◀
        </button>
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="input-field flex-1 text-center rounded-none disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => onChange(adjustTime(value, 30))}
          disabled={disabled}
          className="px-2 py-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 rounded-r-lg transition disabled:opacity-50"
          title="+30 min"
        >
          ▶
        </button>
      </div>
    </div>
  );
};

// Determine workout status for display
// SINGLE SOURCE OF TRUTH: workout.status (not booking time)
const getWorkoutStatus = (booking, workout, t) => {
  if (!workout) return { status: 'no_workout', label: t('no_workout'), color: 'bg-gray-500/20 text-gray-300' };
  
  // Finished flag is the definitive completion signal
  if (workout.is_finished || workout.status === 'completed') {
    return { status: 'completed', label: t('completed_status'), color: 'bg-green-500/20 text-green-200' };
  }
  
  // In progress — only when workout.status is explicitly "in_progress"
  if (workout.status === 'in_progress') {
    return { status: 'in_progress', label: t('in_progress'), color: 'bg-yellow-500/20 text-yellow-200' };
  }
  
  // Everything else is planned/not started
  return { status: 'planned', label: t('planned'), color: 'bg-blue-500/20 text-blue-200' };
};

// Determine editor mode based on workout state and booking time
// CRITICAL: workout.status is the single source of truth for editor mode
const getEditorMode = (booking, workout) => {
  if (!workout) return 'plan';
  
  // COMPLETED: is_finished = true → Results mode (correction only)
  if (workout.is_finished || workout.status === 'completed') {
    return 'results';
  }
  
  // IN PROGRESS: Active session → Entry mode (recording sets/reps)
  if (workout.status === 'in_progress') {
    return 'entry';
  }
  
  // PLANNED/SCHEDULED: Not started → Full edit mode
  return 'plan';
};

// Calculate estimated 1RM using Brzycki formula simplified
const calculateEstimated1RM = (weight, reps) => {
  if (!weight || !reps || reps <= 0) return 0;
  return weight * (1 + reps / 30);
};

// Get best set from actual_sets using 1RM estimation (not just weight)
// Returns only ONE best set per exercise
const getBestSet = (exercise) => {
  const actualSets = exercise.actual_sets || [];
  if (actualSets.length === 0) return null;
  
  let bestSet = null;
  let bestScore = 0;
  let bestIndex = -1;
  
  actualSets.forEach((set, index) => {
    const weight = set.weight || 0;
    const reps = set.reps || 0;
    const score = calculateEstimated1RM(weight, reps);
    
    if (score > bestScore) {
      bestScore = score;
      bestSet = { ...set, estimated1RM: score };
      bestIndex = index;
    }
  });
  
  return bestSet ? { ...bestSet, index: bestIndex } : null;
};

// Format day name
const getDayName = (dateStr) => {
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } catch {
    return '';
  }
};

// ============================================
// BOOKING ROW COMPONENT (Expandable)
// ============================================
const BookingRow = ({ 
  booking, 
  workout, 
  isExpanded, 
  onToggleExpand,
  onEditBooking,
  onDeleteBooking,
  onCreateWorkout,
  onUseTemplate,
  onViewWorkout,
  onEditWorkout,
  onSaveAsTemplate,
  onViewClientWeek,
  isAdmin,
  templateCounts,
  exerciseLibrary, // NEW: Exercise library for images and categories
  t 
}) => {
  // Get template execution count — only when workout has reliable template_id
  const getTemplateExecutionCount = () => {
    if (!workout?.template_id || !booking?.client_id) return null;
    const clientCounts = templateCounts?.[booking.client_id] || [];
    const templateInfo = clientCounts.find(tc => tc.template_name === workout.template_name);
    return templateInfo?.count || null;
  };
  
  const executionCount = getTemplateExecutionCount();
  const workoutInfo = getWorkoutStatus(booking, workout, t);
  const exerciseCount = workout?.exercises?.length || 0;
  
  // Helper to get exercise info from library (for images and categories)
  const getExerciseInfo = (exerciseId) => {
    if (!exerciseLibrary) return {};
    return exerciseLibrary.find(e => e.id === exerciseId) || {};
  };
  
  // Calculate metrics for expanded view
  const totalVolume = workout?.exercises?.reduce((sum, ex) => {
    const w = ex.target_weight || ex.weight || 0;
    const s = ex.target_sets || ex.sets || 3;
    const r = ex.target_reps || ex.reps || 12;
    return sum + (w * s * r);
  }, 0) || 0;
  
  // Group exercises by superset
  const groupedExercises = useMemo(() => {
    if (!workout?.exercises) return [];
    
    const groups = [];
    let currentGroup = null;
    
    workout.exercises.forEach((ex, idx) => {
      if (ex.is_superset) {
        const groupKey = ex.superset_group || 'A';
        if (!currentGroup || currentGroup.groupKey !== groupKey) {
          currentGroup = { groupKey, exercises: [], isSuperset: true };
          groups.push(currentGroup);
        }
        currentGroup.exercises.push({ ...ex, originalIndex: idx });
      } else {
        groups.push({ exercises: [{ ...ex, originalIndex: idx }], isSuperset: false });
        currentGroup = null;
      }
    });
    
    return groups;
  }, [workout?.exercises]);

  return (
    <div className="glass-card overflow-hidden">
      {/* Collapsed Row - Always visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-blue-500/10 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center justify-between">
          {/* Left: Expand arrow + Client + Date/Time */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <span className={`text-blue-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
              ▶
            </span>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <span className="text-white font-semibold">{booking.client_name || 'Unknown Client'}</span>
                <span className="text-blue-300 text-sm">
                  {getDayName(booking.date)} {booking.date}
                </span>
                <span className="text-blue-400 text-sm">
                  {booking.start_time} – {booking.end_time}
                </span>
              </div>
              
              {/* Status badges */}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  booking.status === 'approved' ? 'bg-green-500/20 text-green-200' :
                  booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-200' :
                  booking.status === 'completed' ? 'bg-blue-500/20 text-blue-200' :
                  'bg-red-500/20 text-red-200'
                }`}>
                  {booking.status}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${workoutInfo.color}`}>
                  {workoutInfo.label}
                </span>
                {executionCount && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-200" title={`Completado ${executionCount} veces por este cliente`}>
                    🔁 {executionCount}× completado
                  </span>
                )}
                {exerciseCount > 0 && (
                  <span className="text-blue-400 text-xs">
                    {exerciseCount} exercises
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Right: Action buttons - Visually grouped */}
          <div className="flex items-center gap-1 ml-4" onClick={(e) => e.stopPropagation()}>
            {/* ═══════════════════════════════════════════
                WORKOUT ACTIONS GROUP (Left side)
                Icons: + (add), 👁 (view), 🏋️ (edit), 📄 (template)
            ═══════════════════════════════════════════ */}
            <div className="flex items-center gap-1 pr-2 border-r border-slate-600/50">
              {workoutInfo.status === 'no_workout' ? (
                <>
                  {/* Add Workout */}
                  <button
                    onClick={() => onCreateWorkout(booking)}
                    data-testid={`create-workout-btn-${booking.id}`}
                    className="p-2 rounded bg-green-500/20 hover:bg-green-500/30 text-green-200 transition"
                    title="Add Workout"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  {/* Use Template */}
                  <button
                    onClick={() => onUseTemplate(booking)}
                    data-testid={`use-template-btn-${booking.id}`}
                    className="p-2 rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 transition"
                    title="Use Template"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  {/* View Workout */}
                  <button
                    onClick={() => onViewWorkout(booking, workout)}
                    data-testid={`view-workout-btn-${booking.id}`}
                    className="p-2 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 transition"
                    title="View Workout"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  {/* Edit Workout - DUMBBELL icon */}
                  <button
                    onClick={() => onEditWorkout(booking, workout)}
                    data-testid={`edit-workout-btn-${booking.id}`}
                    className="p-2 rounded bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 transition"
                    title={workoutInfo.status === 'completed' ? 'Edit Results' : 'Edit Workout'}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/>
                    </svg>
                  </button>
                  {/* Save as Template */}
                  {(workoutInfo.status === 'planned' || workoutInfo.status === 'completed') && (
                    <button
                      onClick={() => onSaveAsTemplate(booking, workout)}
                      data-testid={`save-template-btn-${booking.id}`}
                      className="p-2 rounded bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 transition"
                      title="Save as Template"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>

            {/* ═══════════════════════════════════════════
                BOOKING ACTIONS GROUP (Right side)
                Icons: 📅 (planner), ✏️ (edit), 🗑️ (delete)
            ═══════════════════════════════════════════ */}
            <div className="flex items-center gap-1 pl-2">
              {/* View Client Planner - Calendar icon */}
              {onViewClientWeek && (
                <button
                  onClick={() => onViewClientWeek(booking.client_id, booking.date)}
                  data-testid={`view-planner-btn-${booking.id}`}
                  className="p-2 rounded bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 transition"
                  title="View Client Planner"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
              
              {/* Edit Booking - Pencil icon (ONLY for booking) */}
              <button
                onClick={() => onEditBooking(booking)}
                data-testid={`edit-booking-btn-${booking.id}`}
                className="p-2 rounded bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 transition"
                title="Edit Booking"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              
              {/* Delete Booking - Trash icon */}
              {(isAdmin || workoutInfo.status !== 'completed') && (
                <button
                  onClick={() => onDeleteBooking(booking)}
                  data-testid={`delete-booking-btn-${booking.id}`}
                  className="p-2 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition"
                  title={isAdmin ? "Delete Session (Admin)" : "Delete Booking"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Expanded Content - Matching Templates Design */}
      {isExpanded && workout && workout.exercises && workout.exercises.length > 0 && (
        <div className="border-t border-blue-400/20 p-4 bg-blue-950/30">
          {/* Metrics Summary */}
          <div className="flex items-center gap-4 mb-4 text-sm">
            <span className="text-blue-300">
              📊 {exerciseCount} exercises
            </span>
            {totalVolume > 0 && (
              <span className="text-blue-300">
                📈 {totalVolume.toLocaleString()} kg volume
              </span>
            )}
            {workout.total_time > 0 && (
              <span className="text-blue-300">
                ⏱️ {workout.total_time} min
              </span>
            )}
            {workout.exercises.some(ex => ex.is_superset) && (
              <span className="text-orange-300">
                🔗 Supersets
              </span>
            )}
          </div>
          
          {/* Exercise List - Matching Templates Design with thumbnails & category tags */}
          <div className="space-y-2">
            {workout.exercises.map((ex, idx) => {
              const exInfo = getExerciseInfo(ex.exercise_id);
              // Priority: library data (has animation_webp) > workout embedded data
              const imageUrl = getExerciseAnimationUrl(exInfo) || getExerciseAnimationUrl(ex);
              const category = exInfo.category;
              const categoryColor = getCategoryColor(category);
              const targetWeight = ex.target_weight || ex.weight || 0;
              const targetReps = ex.target_reps || ex.reps || 12;
              const targetSets = ex.target_sets || ex.sets || 3;
              const bestSet = getBestSet(ex);
              
              return (
                <div 
                  key={idx}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    ex.is_superset ? 'bg-orange-900/20 border-l-2 border-orange-400' : 'bg-blue-900/20'
                  }`}
                >
                  {/* Order Number */}
                  <span className="text-blue-500 font-mono text-sm w-6 text-center flex-shrink-0">
                    {idx + 1}.
                  </span>
                  
                  {/* Thumbnail */}
                  <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-blue-800/50">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={ex.exercise_name} 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg">🏋️</div>
                    )}
                  </div>
                  
                  {/* Exercise Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium truncate">{ex.exercise_name}</span>
                      {ex.is_superset && (
                        <span className="text-orange-400 text-xs">🔗{ex.superset_group || 'A'}</span>
                      )}
                    </div>
                    <div className="text-blue-400 text-xs">
                      {targetSets}×{targetReps}
                      {targetWeight > 0 && ` · ${targetWeight}kg`}
                      {ex.rest_time > 0 && ` · ${ex.rest_time}s rest`}
                    </div>
                    
                    {/* Best Set if exists with Est. 1RM */}
                    {bestSet && (
                      <div className="mt-1 text-xs">
                        <span className="text-yellow-400">⭐ Best: {bestSet.reps}×{bestSet.weight}kg</span>
                        <span className="text-yellow-500/70 ml-1">(Est. 1RM: {bestSet.estimated1RM.toFixed(1)}kg)</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Category Tag */}
                  {category && (
                    <span className={`px-2 py-0.5 rounded text-xs ${categoryColor.bg} ${categoryColor.text} flex-shrink-0 hidden sm:inline`}>
                      {categoryColor.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Empty expanded state */}
      {isExpanded && (!workout || !workout.exercises || workout.exercises.length === 0) && (
        <div className="border-t border-blue-400/20 p-4 bg-blue-900/20 text-center">
          <p className="text-blue-400 text-sm">No workout assigned yet</p>
          <div className="flex justify-center gap-2 mt-3">
            <button
              onClick={() => onCreateWorkout(booking)}
              className="btn-primary text-sm"
            >
              ➕ Create Workout
            </button>
            <button
              onClick={() => onUseTemplate(booking)}
              className="btn-secondary text-sm"
            >
              📋 Use Template
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// BOOKING EDIT MODAL
// ============================================
const BookingEditModal = ({ booking, onSave, onClose, t }) => {
  const [formData, setFormData] = useState({
    status: booking.status || 'pending',
    trainer_notes: booking.trainer_notes || '',
    date: booking.date || '',
    start_time: booking.start_time || '',
    end_time: booking.end_time || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate end time > start time
    if (formData.start_time >= formData.end_time) {
      alert('End time must be after start time');
      return;
    }
    
    onSave(booking, formData);
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div className="glass-card max-w-lg w-full my-4 sm:my-8 p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4 pb-4 border-b border-blue-400/20">
            <h3 className="text-lg font-bold text-white">🗓️ Edit Booking</h3>
            <button onClick={onClose} className="text-red-400 hover:text-red-300 text-xl font-bold">×</button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client info (read-only) */}
          <div className="bg-blue-900/30 rounded-lg p-3">
            <p className="text-blue-300 text-xs">Client</p>
            <p className="text-white font-semibold">{booking.client_name}</p>
            {booking.client_email && (
              <p className="text-blue-400 text-sm">{booking.client_email}</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="input-field w-full"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="input-field w-full"
              />
            </div>
            
            <TimePicker
              value={formData.start_time}
              onChange={(time) => setFormData({ ...formData, start_time: time })}
              label="Start Time"
            />
            
            <TimePicker
              value={formData.end_time}
              onChange={(time) => setFormData({ ...formData, end_time: time })}
              label="End Time"
            />
          </div>
          
          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">Trainer Notes</label>
            <textarea
              value={formData.trainer_notes}
              onChange={(e) => setFormData({ ...formData, trainer_notes: e.target.value })}
              className="input-field w-full"
              rows="3"
              placeholder="Add notes for this booking..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save Changes
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
  
  return ReactDOM.createPortal(modalContent, document.body);
};

// ============================================
// WORKOUT EDITOR MODAL (Plan / Live / Results modes)
// ============================================
const WorkoutEditorModal = ({ 
  booking, 
  workout, 
  mode, // 'plan' | 'live' | 'results'
  exercises: allExercises, 
  templates,
  onSave, 
  onClose, 
  onRefresh,
  t 
}) => {
  const [workoutExercises, setWorkoutExercises] = useState([]);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [workoutTimes, setWorkoutTimes] = useState({ total_time: 0, rest_time: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [appliedTemplateOrigin, setAppliedTemplateOrigin] = useState(null); // Track template origin for save
  
  // UNIFIED FILTER STATES - Same as Exercise Library
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [muscleFilter, setMuscleFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  
  const isResultsMode = mode === 'results';
  const isLiveMode = mode === 'live';
  const isPlanMode = mode === 'plan';
  
  // Initialize exercises from workout
  useEffect(() => {
    if (workout?.exercises) {
      setWorkoutExercises(workout.exercises.map(ex => ({
        ...ex,
        actual_sets: ex.actual_sets || []
      })));
      setWorkoutTimes({
        total_time: workout.total_time || 0,
        rest_time: workout.rest_time || 0
      });
    } else {
      setWorkoutExercises([]);
      setWorkoutTimes({ total_time: 0, rest_time: 0 });
    }
  }, [workout]);
  
  // Category options - SAME AS Exercise Library
  const CATEGORIES = [
    { value: 'all', label: t('all_categories') || 'Todas' },
    { value: 'chest', label: t('chest') || 'Pecho' },
    { value: 'back', label: t('back') || 'Espalda' },
    { value: 'legs', label: t('legs') || 'Piernas' },
    { value: 'shoulders', label: t('shoulders') || 'Hombros' },
    { value: 'arms', label: t('arms') || 'Brazos' },
    { value: 'core', label: t('core') || 'Core' },
    { value: 'cardio', label: t('cardio') || 'Cardio' },
    { value: 'full-body', label: t('full-body') || 'Cuerpo completo' },
    { value: 'stretching', label: t('stretching') || 'Estiramientos' },
    { value: 'plyometrics', label: t('plyometrics') || 'Pliometría' },
    { value: 'neck', label: t('neck') || 'Cuello' }
  ];

  // Primary muscle groups
  const PRIMARY_MUSCLES = ['Quadriceps', 'Glutes', 'Hamstrings', 'Calves', 'Chest', 'Back', 'Lats', 'Deltoids', 'Biceps', 'Triceps', 'Forearms', 'Abs', 'Obliques', 'Core', 'Trapezius', 'Rhomboids'];
  
  // UNIFIED FILTER LOGIC - Exactly like Exercise Library
  const filteredExercises = (() => {
    let filtered = searchExercises(searchTerm, allExercises, 'es');
    return filtered.filter(exercise => {
      if (categoryFilter !== 'all' && exercise.category !== categoryFilter) {
        return false;
      }
      if (muscleFilter !== 'all') {
        const hasMuscle = exercise.primary_muscle_groups && exercise.primary_muscle_groups.includes(muscleFilter);
        if (!hasMuscle) return false;
      }
      if (difficultyFilter !== 'all' && exercise.difficulty !== difficultyFilter) {
        return false;
      }
      return true;
    });
  })();

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setMuscleFilter('all');
    setDifficultyFilter('all');
  };
  
  // Add exercise to workout (only in plan/live mode)
  const addExercise = (exercise) => {
    if (isResultsMode) return;
    
    const newExercise = {
      exercise_id: exercise.id,
      exercise_name: exercise.name,
      target_sets: 4,
      target_reps: 12,
      target_weight: 0,
      rest_time: 120,
      instructions: exercise.instructions,
      instructions_es: exercise.instructions_es,
      image_url: exercise.image_url,
      animation_webp: exercise.animation_webp,
      muscle_groups: exercise.muscle_groups,
      difficulty: exercise.difficulty,
      equipment: exercise.equipment,
      is_superset: false,
      superset_group: null,
      actual_sets: []
    };
    setWorkoutExercises([...workoutExercises, newExercise]);
    setShowExerciseSelector(false);
  };
  
  // Update exercise field
  const updateExercise = (index, field, value) => {
    // In results mode, only allow updating actual_sets
    if (isResultsMode && !['actual_sets', 'notes'].includes(field)) return;
    
    const updated = [...workoutExercises];
    updated[index][field] = value;
    setWorkoutExercises(updated);
  };
  
  // Remove exercise (not in results mode)
  const removeExercise = (index) => {
    if (isResultsMode) return;
    
    // In live mode, only allow removal if no sets recorded
    if (isLiveMode && workoutExercises[index].actual_sets?.length > 0) {
      alert('Cannot remove exercise with recorded sets');
      return;
    }
    
    setWorkoutExercises(workoutExercises.filter((_, i) => i !== index));
  };
  
  // Toggle superset (not in results mode)
  const toggleSuperset = (index) => {
    if (isResultsMode) return;
    
    const updated = [...workoutExercises];
    const willBeSuperset = !updated[index].is_superset;
    updated[index] = {
      ...updated[index],
      is_superset: willBeSuperset,
      // Clear group when turning superset OFF; default to 'A' when turning ON
      superset_group: willBeSuperset ? (updated[index].superset_group || 'A') : null,
    };
    setWorkoutExercises(updated);
  };
  
  // Add actual set
  const addActualSet = (exerciseIndex) => {
    const updated = [...workoutExercises];
    const ex = updated[exerciseIndex];
    const lastSet = ex.actual_sets[ex.actual_sets.length - 1];
    updated[exerciseIndex].actual_sets.push({
      reps: lastSet?.reps || ex.target_reps || 12,
      weight: lastSet?.weight || ex.target_weight || 0,
      rest_time: lastSet?.rest_time || ex.rest_time || 120
    });
    setWorkoutExercises(updated);
  };
  
  // Update actual set
  const updateActualSet = (exerciseIndex, setIndex, field, value) => {
    const updated = [...workoutExercises];
    updated[exerciseIndex].actual_sets[setIndex][field] = 
      field === 'rest_time' ? parseInt(value) || 0 : parseFloat(value) || 0;
    setWorkoutExercises(updated);
  };
  
  // Remove actual set
  const removeActualSet = (exerciseIndex, setIndex) => {
    const updated = [...workoutExercises];
    updated[exerciseIndex].actual_sets.splice(setIndex, 1);
    setWorkoutExercises(updated);
  };
  
  // Apply template
  const applyTemplate = (template) => {
    if (isResultsMode) return;
    
    const templateExercises = template.exercises.map(ex => ({
      exercise_id: ex.exercise_id,
      exercise_name: ex.exercise_name,
      target_sets: ex.sets ?? ex.target_sets ?? 4,
      target_reps: ex.reps ?? ex.target_reps ?? 12,
      target_weight: ex.weight ?? ex.target_weight ?? 0,
      rest_time: ex.rest_time || 120,
      instructions: ex.instructions || '',
      instructions_es: ex.instructions_es || '',
      image_url: ex.image_url || '',
      animation_webp: ex.animation_webp || '',
      muscle_groups: ex.muscle_groups || [],
      difficulty: ex.difficulty || '',
      equipment: ex.equipment || '',
      is_superset: ex.is_superset || false,
      superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
      actual_sets: []
    }));
    setWorkoutExercises(templateExercises);
    setAppliedTemplateOrigin({
      template_id: template.id,
      template_name: template.name || template.template_name || 'Unnamed Template'
    });
    setShowTemplates(false);
  };
  
  // Save workout
  const handleSave = async () => {
    if (workoutExercises.length === 0 && !isResultsMode) {
      alert('Please add at least one exercise');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(booking, workout, workoutExercises, workoutTimes, mode, appliedTemplateOrigin);
      onClose();
    } catch (error) {
      console.error('Error saving workout:', error);
      alert('Error saving workout: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const modeLabels = {
    plan: '✏️ Plan Workout',
    live: '🏃 Live Workout',
    results: '📊 Edit Results'
  };
  
  const modeDescriptions = {
    plan: 'Design the workout plan. Add exercises, set targets, create supersets.',
    live: 'Workout in progress. Log sets as performed. Can add exercises.',
    results: 'Workout completed. Edit recorded data only. Structure is locked.'
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div className="glass-card max-w-4xl w-full p-6 my-4 sm:my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="sticky top-0 bg-blue-900/80 -mx-6 -mt-6 px-6 py-4 mb-4 border-b border-blue-400/20 flex justify-between items-center z-10">
            <div>
              <h3 className="text-lg font-bold text-white">{modeLabels[mode]}</h3>
              <p className="text-blue-300 text-sm">{booking.client_name} • {booking.date}</p>
            </div>
            <button onClick={onClose} className="text-red-400 hover:text-red-300 text-xl font-bold">×</button>
          </div>
          
          {/* Mode indicator */}
          <div className={`rounded-lg p-3 mb-4 ${
            isResultsMode ? 'bg-green-900/20 border border-green-500/20' :
          isLiveMode ? 'bg-yellow-900/20 border border-yellow-500/20' :
          'bg-blue-900/20 border border-blue-500/20'
        }`}>
          <p className={`text-sm ${
            isResultsMode ? 'text-green-300' : isLiveMode ? 'text-yellow-300' : 'text-blue-300'
          }`}>
            {modeDescriptions[mode]}
          </p>
        </div>
        
        {/* Time Metrics (for live/results mode) */}
        {(isLiveMode || isResultsMode) && (
          <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4 mb-4">
            <h4 className="text-green-200 font-medium mb-3 text-sm">⏱️ Workout Times (minutes)</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-green-300 text-xs mb-1">Total Time</label>
                <input
                  type="number"
                  value={workoutTimes.total_time}
                  onChange={(e) => setWorkoutTimes(prev => ({ ...prev, total_time: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-green-900/30 border border-green-500/30 rounded text-white text-sm"
                  min="0"
                  step="0.5"
                />
              </div>
              <div>
                <label className="block text-green-300 text-xs mb-1">Rest Time</label>
                <input
                  type="number"
                  value={workoutTimes.rest_time}
                  onChange={(e) => setWorkoutTimes(prev => ({ ...prev, rest_time: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-green-900/30 border border-green-500/30 rounded text-white text-sm"
                  min="0"
                  step="0.5"
                />
              </div>
              <div>
                <label className="block text-green-300 text-xs mb-1">Active Time</label>
                <div className="px-3 py-2 bg-green-900/50 border border-green-500/30 rounded text-white text-sm">
                  {Math.max(0, (workoutTimes.total_time - workoutTimes.rest_time)).toFixed(1)} min
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Action buttons (plan/live mode only) */}
        {!isResultsMode && (
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="btn-secondary"
            >
              📋 Use Template
            </button>
            <button
              onClick={() => setShowExerciseSelector(!showExerciseSelector)}
              className="btn-primary"
            >
              + Add Exercise
            </button>
          </div>
        )}
        
        {/* Template Selector */}
        {showTemplates && !isResultsMode && (
          <div className="bg-blue-900/30 p-4 rounded-lg mb-4">
            <h5 className="text-white font-medium mb-3">Select Template:</h5>
            {templates.length === 0 ? (
              <p className="text-blue-300 text-center py-4">No templates available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                {templates.map((template) => (
                  <div 
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="text-left p-3 bg-blue-800/30 hover:bg-blue-700/40 rounded-lg transition-all cursor-pointer"
                  >
                    <div className="text-white font-medium mb-1">
                      📋 {template.name || template.template_name || 'Unnamed Template'}
                    </div>
                    <div className="text-blue-300 text-sm">
                      {template.exercises?.length || 0} exercises
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowTemplates(false)} className="btn-secondary mt-3">
              Cancel
            </button>
          </div>
        )}
        
        {/* Exercise Selector */}
        {showExerciseSelector && !isResultsMode && (
          <div className="bg-blue-900/30 p-4 rounded-lg mb-4">
            <div className="flex justify-between items-center mb-3">
              <h5 className="text-white font-medium">Add Exercise:</h5>
              <button onClick={() => setShowExerciseSelector(false)} className="text-blue-300 hover:text-white">
                ✕ Close
              </button>
            </div>
            
            {/* UNIFIED FILTERS - Same as Exercise Library */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
              {/* Search by Name */}
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">🔍 {t('search_by_name') || 'Buscar'}</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('search_exercises')}
                  className="input-field text-sm"
                />
              </div>

              {/* Category filter - PRIMARY */}
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">📂 {t('category') || 'Categoría'}</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="input-field text-sm"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Primary Muscle filter */}
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">💪 {t('primary_muscle') || 'Músculo'}</label>
                <select
                  value={muscleFilter}
                  onChange={(e) => setMuscleFilter(e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="all">{t('all_muscles') || 'Todos'}</option>
                  {PRIMARY_MUSCLES.map(muscle => (
                    <option key={muscle} value={muscle}>{muscle}</option>
                  ))}
                </select>
              </div>

              {/* Difficulty filter */}
              <div>
                <label className="block text-blue-200 text-xs font-medium mb-1">⭐ {t('difficulty') || 'Dificultad'}</label>
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="input-field text-sm"
                >
                  <option value="all">{t('all_difficulties') || 'Todas'}</option>
                  <option value="beginner">{t('beginner') || 'Principiante'}</option>
                  <option value="intermediate">{t('intermediate') || 'Intermedio'}</option>
                  <option value="advanced">{t('advanced') || 'Avanzado'}</option>
                </select>
              </div>
            </div>

            {/* Clear Filters + Results Count */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={clearFilters}
                className="px-3 py-1 rounded text-sm bg-gray-600/40 text-gray-200 hover:bg-gray-500/40"
              >
                🔄 {t('clear_filters') || 'Limpiar filtros'}
              </button>
              <p className="text-blue-300 text-sm">
                {filteredExercises.length} de {allExercises.length} ejercicios
                {categoryFilter !== 'all' && ` (${categoryFilter})`}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
              {filteredExercises.slice(0, 50).map((exercise) => (
                <button
                  key={exercise.id}
                  onClick={() => addExercise(exercise)}
                  className="text-left p-3 bg-blue-800/30 hover:bg-blue-700/40 rounded-lg transition-all"
                >
                  <div className="flex items-center space-x-3">
                    {(exercise.animation_webp || exercise.image_url) && (
                      <img
                        src={getExerciseAnimationUrl(exercise)}
                        alt={exercise.name}
                        className="w-10 h-10 object-cover rounded"
                      />
                    )}
                    <div>
                      <div className="text-white font-medium text-sm">{exercise.name}</div>
                      <div className="text-blue-300 text-xs">{t(exercise.category) || exercise.category}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <button onClick={() => setShowExerciseSelector(false)} className="btn-secondary mt-3">
              Cancel
            </button>
          </div>
        )}
        
        {/* Exercise List */}
        <div className="space-y-4">
          {workoutExercises.map((exercise, index) => (
            <div key={index} className={`p-4 rounded-lg border-l-4 ${
              exercise.is_superset ? 'bg-orange-900/10 border-orange-400' : 'bg-blue-900/20 border-blue-400'
            }`}>
              {/* Exercise Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-3">
                  {(exercise.animation_webp || exercise.image_url) && (
                    <img
                      src={getExerciseAnimationUrl(exercise)}
                      alt={exercise.exercise_name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  )}
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-400 font-mono text-sm">{index + 1}.</span>
                      <h5 className="text-white font-semibold">{exercise.exercise_name}</h5>
                      {exercise.is_superset && (
                        <span className="bg-orange-500/20 text-orange-200 px-2 py-0.5 rounded text-xs">
                          🔗 {exercise.superset_group}
                        </span>
                      )}
                    </div>
                    <p className="text-blue-300 text-sm">{exercise.equipment}</p>
                  </div>
                </div>
                
                {/* Exercise actions (not in results mode) */}
                {!isResultsMode && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleSuperset(index)}
                      className={`text-xs px-3 py-1 rounded ${
                        exercise.is_superset 
                          ? 'bg-orange-500/30 text-orange-200' 
                          : 'bg-blue-500/20 text-blue-300'
                      }`}
                    >
                      {exercise.is_superset ? '🔗 Superset' : '+ Superset'}
                    </button>
                    {exercise.is_superset && (
                      <select
                        value={exercise.superset_group || 'A'}
                        onChange={(e) => updateExercise(index, 'superset_group', e.target.value)}
                        className="text-xs px-2 py-1 rounded bg-orange-900/30 text-orange-200"
                      >
                        {['A', 'B', 'C', 'D', 'E'].map(g => (
                          <option key={g} value={g}>Group {g}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => removeExercise(index)}
                      className="text-red-400 hover:text-red-300"
                      disabled={isLiveMode && exercise.actual_sets?.length > 0}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              
              {/* Target Parameters (editable in plan mode, read-only otherwise) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-blue-200 text-xs mb-1">Target Sets</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={exercise.target_sets || exercise.sets || 4}
                    onChange={(e) => updateExercise(index, 'target_sets', parseInt(e.target.value))}
                    disabled={isResultsMode}
                    className="w-full bg-blue-900/30 text-white border border-blue-400/30 rounded px-2 py-1 text-sm disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-blue-200 text-xs mb-1">Target Reps</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={exercise.target_reps || exercise.reps || 12}
                    onChange={(e) => updateExercise(index, 'target_reps', parseInt(e.target.value))}
                    disabled={isResultsMode}
                    className="w-full bg-blue-900/30 text-white border border-blue-400/30 rounded px-2 py-1 text-sm disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-blue-200 text-xs mb-1">Target Weight (kg)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={exercise.target_weight || 0}
                    onChange={(e) => updateExercise(index, 'target_weight', parseFloat(e.target.value))}
                    disabled={isResultsMode}
                    className="w-full bg-blue-900/30 text-white border border-blue-400/30 rounded px-2 py-1 text-sm disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-blue-200 text-xs mb-1">Rest (sec)</label>
                  <input
                    type="number"
                    min="30"
                    max="300"
                    step="15"
                    value={exercise.rest_time || 120}
                    onChange={(e) => updateExercise(index, 'rest_time', parseInt(e.target.value))}
                    disabled={isResultsMode}
                    className="w-full bg-blue-900/30 text-white border border-blue-400/30 rounded px-2 py-1 text-sm disabled:opacity-50"
                  />
                </div>
              </div>
              
              {/* Actual Sets (for live/results mode) */}
              {(isLiveMode || isResultsMode) && (
                <div className="mt-3 pt-3 border-t border-blue-400/20">
                  <p className="text-blue-300 text-xs font-medium mb-2">Performed Sets:</p>
                  <div className="space-y-2">
                    {exercise.actual_sets?.map((set, setIdx) => (
                      <div key={setIdx} className="flex items-center gap-2 bg-blue-800/30 p-2 rounded">
                        <span className="text-blue-400 text-xs w-12">Set {setIdx + 1}</span>
                        <input
                          type="number"
                          value={set.reps}
                          onChange={(e) => updateActualSet(index, setIdx, 'reps', e.target.value)}
                          className="w-16 px-2 py-1 bg-blue-900/50 border border-blue-500/30 rounded text-white text-sm"
                          placeholder="Reps"
                        />
                        <span className="text-blue-300 text-xs">×</span>
                        <input
                          type="number"
                          value={set.weight}
                          onChange={(e) => updateActualSet(index, setIdx, 'weight', e.target.value)}
                          className="w-20 px-2 py-1 bg-blue-900/50 border border-blue-500/30 rounded text-white text-sm"
                          step="0.5"
                          placeholder="Weight"
                        />
                        <span className="text-blue-300 text-xs">kg</span>
                        <span className="text-blue-400 text-xs ml-2">Rest:</span>
                        <input
                          type="number"
                          value={set.rest_time}
                          onChange={(e) => updateActualSet(index, setIdx, 'rest_time', e.target.value)}
                          className="w-16 px-2 py-1 bg-blue-900/50 border border-blue-500/30 rounded text-white text-sm"
                          placeholder="Rest"
                        />
                        <span className="text-blue-300 text-xs">s</span>
                        <button
                          onClick={() => removeActualSet(index, setIdx)}
                          className="ml-auto text-red-400 hover:text-red-300 text-sm px-2"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    
                    {(!exercise.actual_sets || exercise.actual_sets.length === 0) && (
                      <p className="text-blue-400/60 text-xs italic py-2">No sets recorded yet</p>
                    )}
                    
                    <button
                      onClick={() => addActualSet(index)}
                      className="text-xs text-blue-300 hover:text-blue-200 mt-2"
                    >
                      + Add Set
                    </button>
                  </div>
                </div>
              )}
              
              {/* Notes */}
              <div className="mt-3">
                <label className="block text-blue-200 text-xs mb-1">Notes</label>
                <textarea
                  value={exercise.notes || ''}
                  onChange={(e) => updateExercise(index, 'notes', e.target.value)}
                  className="w-full bg-blue-900/30 text-white border border-blue-400/30 rounded px-2 py-1 text-sm"
                  rows="1"
                  placeholder="Notes for this exercise..."
                />
              </div>
            </div>
          ))}
          
          {workoutExercises.length === 0 && (
            <div className="text-center py-8">
              <p className="text-blue-300 mb-4">No exercises added yet</p>
              {!isResultsMode && (
                <button onClick={() => setShowExerciseSelector(true)} className="btn-primary">
                  + Add First Exercise
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-blue-400/20">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? '⏳ Saving...' : '💾 Save'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
  
  return ReactDOM.createPortal(modalContent, document.body);
};

// ============================================
// SAVE AS TEMPLATE MODAL
// ============================================
const SaveTemplateModal = ({ booking, workout, onSave, onClose }) => {
  const [templateName, setTemplateName] = useState(`${booking.client_name} - ${booking.date}`);
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(workout, templateName.trim());
      onClose();
    } catch (error) {
      alert('Error saving template: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Modal onClose={onClose} maxWidth="max-w-lg">
      <Modal.Header onClose={onClose}>
        <h3 className="text-lg font-bold text-white">📋 Save as Template</h3>
      </Modal.Header>
      
      <Modal.Body>
        <div className="bg-blue-900/30 rounded-lg p-4 mb-4">
          <p className="text-blue-300 text-xs mb-1">Creating template from</p>
          <p className="text-white font-semibold">{booking.client_name} - {booking.date}</p>
          <p className="text-blue-300 text-sm mt-1">{workout.exercises?.length || 0} exercises</p>
        </div>
        
        <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-3 mb-4">
          <p className="text-green-200 text-xs font-medium mb-2">✓ Will be saved to template:</p>
          <ul className="text-green-300 text-xs space-y-1">
            <li>• Exercise list & order</li>
            <li>• Target sets, reps & weights</li>
            <li>• Rest times & notes</li>
            <li>• Superset groupings</li>
          </ul>
        </div>
        
        <div>
          <label className="block text-blue-200 text-sm font-medium mb-2">Template Name *</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="input-field w-full"
            placeholder="e.g., Upper Body Push Day"
          />
        </div>
      </Modal.Body>
      
      <Modal.Footer className="flex gap-3">
        <button onClick={onClose} className="btn-secondary flex-1">
          Cancel
        </button>
        <button 
          onClick={handleSave} 
          disabled={isSaving || !templateName.trim()}
          className="btn-primary flex-1"
        >
          {isSaving ? '⏳ Saving...' : '📋 Save Template'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const ITEMS_PER_PAGE = 50;

const UnifiedBookingsTab = ({ bookings: initialBookings, clients, exercises, onUpdateBooking, onRefresh, userRole, t, onViewClientWeek }) => {
  const isAdmin = userRole === 'administrator';
  const [workouts, setWorkouts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [templateCounts, setTemplateCounts] = useState({});
  
  // Pagination state
  const [bookings, setBookings] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Date range filter state
  const [dateRange, setDateRange] = useState('90d'); // Default: Last 90 days
  
  // Modal states
  const [editingBooking, setEditingBooking] = useState(null);
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(null);
  
  // Filters
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workoutFilter, setWorkoutFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  
  // Fetch bookings with pagination
  const fetchBookings = async (offset = 0, append = false) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(
        `${API}/bookings?limit=${ITEMS_PER_PAGE}&offset=${offset}&date_range=${dateRange}`,
        { headers }
      );
      
      const data = response.data;
      
      if (append) {
        setBookings(prev => [...prev, ...(data.bookings || [])]);
      } else {
        setBookings(data.bookings || []);
      }
      
      setHasMore(data.has_more);
      setTotalCount(data.total);
      setCurrentOffset(offset);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  // Initial load and when date range changes
  useEffect(() => {
    const loadInitial = async () => {
      setInitialLoading(true);
      setCurrentOffset(0);
      await fetchBookings(0, false);
      setInitialLoading(false);
    };
    loadInitial();
  }, [dateRange]);

  // Handle "Show More"
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const newOffset = currentOffset + ITEMS_PER_PAGE;
    await fetchBookings(newOffset, true);
    setLoadingMore(false);
  };

  // Handle refresh - reset to first page
  const handleRefresh = async () => {
    setInitialLoading(true);
    setCurrentOffset(0);
    await fetchBookings(0, false);
    await fetchWorkouts();
    await fetchTemplateCounts();
    setInitialLoading(false);
    if (onRefresh) onRefresh();
  };
  
  const fetchWorkouts = async () => {
    try {
      // Fetch regular workouts
      const workoutsResponse = await axios.get(`${API}/workouts`);
      setWorkouts(workoutsResponse.data.filter(w => w.client_id !== 'template'));
      
      // Fetch templates separately (they're in workout_templates collection)
      const templatesResponse = await axios.get(`${API}/workouts?client_id=template`);
      setTemplates(templatesResponse.data || []);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };
  
  // Fetch template execution counts
  const fetchTemplateCounts = async () => {
    try {
      const response = await axios.get(`${API}/trainer/template-execution-counts`);
      setTemplateCounts(response.data?.client_counts || {});
    } catch (error) {
      console.error('Error fetching template counts:', error);
    }
  };
  
  // Fetch workouts, templates, and counts when bookings change
  useEffect(() => {
    if (bookings.length > 0) {
      fetchWorkouts();
      fetchTemplateCounts();
    }
  }, [bookings.length]);
  
  // Get workout for a booking
  const getWorkoutForBooking = (booking) => {
    return workouts.find(w => w.booking_id === booking.id);
  };
  
  // Get unique clients for filter
  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    bookings.forEach(b => {
      if (b.client_id && b.client_name && !clientMap.has(b.client_id)) {
        clientMap.set(b.client_id, b.client_name);
      }
    });
    return Array.from(clientMap.entries()).map(([id, name]) => ({ id, name }));
  }, [bookings]);
  
  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Workout filter
    if (workoutFilter !== 'all') {
      filtered = filtered.filter(b => {
        const workout = workouts.find(w => w.booking_id === b.id);
        if (workoutFilter === 'no_workout') return !workout;
        if (workoutFilter === 'planned') return workout && (!workout.status || workout.status === 'scheduled');
        if (workoutFilter === 'in_progress') return workout && workout.status === 'in_progress';
        if (workoutFilter === 'completed') return workout && workout.status === 'completed';
        return true;
      });
    }
    
    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(b => b.client_id === clientFilter);
    }
    
    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date + ' ' + (a.start_time || '00:00'));
      const dateB = new Date(b.date + ' ' + (b.start_time || '00:00'));
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [bookings, workouts, statusFilter, workoutFilter, clientFilter, sortOrder]);
  
  // Handlers
  const handleEditBooking = async (booking, formData) => {
    try {
      await onUpdateBooking(booking.id, formData.status, formData.trainer_notes, formData);
      setEditingBooking(null);
      await handleRefresh();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Error updating booking: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };
  
  const handleDeleteBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to delete this booking for ${booking.client_name} on ${booking.date}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/bookings/${booking.id}`);
      await handleRefresh();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Error deleting booking: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };
  
  const handleCreateWorkout = async (booking) => {
    // Open editor in plan mode with no existing workout
    setEditingWorkout({ booking, workout: null, mode: 'plan' });
  };
  
  const handleUseTemplate = (booking) => {
    // Open editor in plan mode - templates will be shown
    setEditingWorkout({ booking, workout: null, mode: 'plan' });
  };
  
  const handleViewWorkout = (booking, workout) => {
    setViewingWorkout({ booking, workout });
  };
  
  const handleEditWorkout = (booking, workout) => {
    const mode = getEditorMode(booking, workout);
    setEditingWorkout({ booking, workout, mode });
  };
  
  const handleSaveAsTemplate = (booking, workout) => {
    setSavingTemplate({ booking, workout });
  };
  
  // Save workout from editor
  const handleSaveWorkout = async (booking, existingWorkout, exerciseData, workoutTimes, mode, templateOrigin) => {
    const safeInt = (val, fallback) => {
      const num = parseInt(val);
      return isNaN(num) ? fallback : num;
    };
    const safeFloat = (val, fallback) => {
      const num = parseFloat(val);
      return isNaN(num) ? fallback : num;
    };
    
    const formattedExercises = exerciseData.map(ex => ({
      exercise_id: ex.exercise_id || '',
      exercise_name: ex.exercise_name || 'Unknown Exercise',
      sets: safeInt(ex.target_sets, safeInt(ex.sets, 4)),
      target_sets: safeInt(ex.target_sets, safeInt(ex.sets, 4)),
      reps: safeInt(ex.target_reps, safeInt(ex.reps, 12)),
      target_reps: safeInt(ex.target_reps, safeInt(ex.reps, 12)),
      weight: safeFloat(ex.target_weight, 0),
      target_weight: safeFloat(ex.target_weight, 0),
      rest_time: safeInt(ex.rest_time, 120),
      notes: ex.notes || '',
      image_url: ex.image_url || '',
      animation_webp: ex.animation_webp || '',
      instructions: ex.instructions || '',
      instructions_es: ex.instructions_es || '',
      muscle_groups: ex.muscle_groups || [],
      difficulty: ex.difficulty || '',
      equipment: ex.equipment || '',
      is_superset: ex.is_superset || false,
      superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null),
      actual_sets: ex.actual_sets || [],
      note: ex.note || '' // Per-exercise notes
    }));
    
    try {
      let workoutId;
      
      if (existingWorkout) {
        workoutId = existingWorkout.id;
      } else {
        // Create new workout
        const response = await axios.post(`${API}/bookings/${booking.id}/create-workout`);
        workoutId = response.data.id;
      }
      
      // Save exercises (with template origin if applied from template)
      let setExercisesUrl = `${API}/workouts/${workoutId}/set-exercises`;
      const origin = templateOrigin;
      if (origin) {
        const params = new URLSearchParams();
        params.append('template_id', origin.template_id);
        params.append('template_name', origin.template_name);
        setExercisesUrl += `?${params.toString()}`;
      }
      await axios.put(setExercisesUrl, formattedExercises);
      
      // Save times if in live/results mode
      if (mode !== 'plan' && (workoutTimes.total_time > 0 || workoutTimes.rest_time > 0)) {
        const activeTime = workoutTimes.total_time - workoutTimes.rest_time;
        await axios.put(`${API}/workouts/${workoutId}`, {
          total_time: workoutTimes.total_time,
          rest_time: workoutTimes.rest_time,
          duration: activeTime > 0 ? activeTime : 0
        });
      }
      
      await fetchWorkouts();
      onRefresh();
    } catch (error) {
      throw error;
    }
  };
  
  // Save template
  // P2 FIX: Use proper template_name field instead of trainer_notes
  const handleSaveTemplateConfirm = async (workout, templateName) => {
    try {
      // Create template workout with proper template_name field
      const response = await axios.post(`${API}/workouts`, {
        client_id: 'template',
        date: new Date().toISOString().split('T')[0],
        template_name: templateName  // Proper field for template name
      });
      
      const newWorkout = response.data;
      
      // Copy exercises (target values only)
      const templateExercises = workout.exercises.map(ex => ({
        exercise_id: ex.exercise_id || '',
        exercise_name: ex.exercise_name || 'Unknown Exercise',
        sets: ex.target_sets ?? ex.sets ?? 4,
        target_sets: ex.target_sets ?? ex.sets ?? 4,
        reps: ex.target_reps ?? ex.reps ?? 12,
        target_reps: ex.target_reps ?? ex.reps ?? 12,
        weight: ex.target_weight ?? 0,
        target_weight: ex.target_weight ?? 0,
        rest_time: ex.rest_time || 120,
        notes: ex.notes || '',
        note: ex.note || '', // Per-exercise notes
        image_url: ex.image_url || '',
        animation_webp: ex.animation_webp || '',
        instructions: ex.instructions || '',
        instructions_es: ex.instructions_es || '',
        muscle_groups: ex.muscle_groups || [],
        difficulty: ex.difficulty || '',
        equipment: ex.equipment || '',
        is_superset: ex.is_superset || false,
        superset_group: ex.is_superset ? (ex.superset_group || 'A') : (ex.superset_group || null)
      }));
      
      await axios.put(`${API}/workouts/${newWorkout.id}/set-exercises`, templateExercises);
      
      alert(`✅ Template "${templateName}" saved successfully!`);
      await fetchWorkouts();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-white">📋 Bookings</h2>
          {totalCount > 0 && (
            <span className="text-sm text-blue-400">
              {bookings.length} of {totalCount} loaded
            </span>
          )}
        </div>
        <button 
          onClick={handleRefresh}
          disabled={initialLoading}
          className="text-sm px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition disabled:opacity-50"
        >
          {initialLoading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>
      
      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          {/* Date Range Filter - NEW */}
          <div>
            <label className="block text-blue-300 text-xs font-medium mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input-field text-sm w-full"
            >
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="12m">Last 12 months</option>
              <option value="all">All time</option>
            </select>
          </div>
          
          <div>
            <label className="block text-blue-300 text-xs font-medium mb-1">Sort by Date</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="input-field text-sm w-full"
            >
              <option value="desc">📅 Most Recent</option>
              <option value="asc">📅 Oldest First</option>
            </select>
          </div>
          
          <div>
            <label className="block text-blue-300 text-xs font-medium mb-1">Booking Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field text-sm w-full"
            >
              <option value="all">All Statuses</option>
              <option value="reserved">📅 Reserved</option>
              <option value="confirmed">✔️ Confirmed</option>
              <option value="assigned">📋 Assigned</option>
              <option value="completed">✅ Completed</option>
              <option value="no_show">❌ No-Show</option>
              <option value="cancelled">🚫 Cancelled</option>
              <option value="pending">⏳ Pending (Legacy)</option>
              <option value="approved">✔️ Approved (Legacy)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-blue-300 text-xs font-medium mb-1">Workout Status</label>
            <select
              value={workoutFilter}
              onChange={(e) => setWorkoutFilter(e.target.value)}
              className="input-field text-sm w-full"
            >
              <option value="all">All Workouts</option>
              <option value="no_workout">⬜ No Workout</option>
              <option value="planned">📝 Planned</option>
              <option value="in_progress">{t('in_progress')}</option>
              <option value="completed">✅ Completed</option>
            </select>
          </div>
          
          <div>
            <label className="block text-blue-300 text-xs font-medium mb-1">Client</label>
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="input-field text-sm w-full"
            >
              <option value="all">All Clients</option>
              {uniqueClients.map(client => (
                <option key={client.id} value={client.id}>👤 {client.name}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSortOrder('desc');
                setStatusFilter('all');
                setWorkoutFilter('all');
                setClientFilter('all');
              }}
              className="btn-secondary w-full text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Booking List */}
      <div className="space-y-3">
        {filteredBookings.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <p className="text-blue-300">No bookings match the selected filters</p>
          </div>
        ) : (
          filteredBookings.map(booking => {
            const workout = getWorkoutForBooking(booking);
            return (
              <BookingRow
                key={booking.id}
                booking={booking}
                workout={workout}
                isExpanded={expandedBookingId === booking.id}
                onToggleExpand={() => setExpandedBookingId(
                  expandedBookingId === booking.id ? null : booking.id
                )}
                onEditBooking={(b) => setEditingBooking(b)}
                onDeleteBooking={handleDeleteBooking}
                onCreateWorkout={handleCreateWorkout}
                onUseTemplate={handleUseTemplate}
                onViewWorkout={handleViewWorkout}
                onEditWorkout={handleEditWorkout}
                onSaveAsTemplate={handleSaveAsTemplate}
                onViewClientWeek={onViewClientWeek}
                isAdmin={isAdmin}
                templateCounts={templateCounts}
                exerciseLibrary={exercises}
                t={t}
              />
            );
          })
        )}
      </div>
      
      {/* Show More Button */}
      {hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-6 py-2.5 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition disabled:opacity-50 font-medium"
            data-testid="load-more-bookings"
          >
            {loadingMore ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Loading...
              </span>
            ) : (
              `Show More (${totalCount - bookings.length} remaining)`
            )}
          </button>
        </div>
      )}
      
      {/* End of list indicator */}
      {!hasMore && bookings.length > ITEMS_PER_PAGE && (
        <div className="text-center py-4 text-blue-400 text-sm">
          — All {totalCount} bookings loaded —
        </div>
      )}
      
      {/* Modals */}
      {editingBooking && (
        <BookingEditModal
          booking={editingBooking}
          onSave={handleEditBooking}
          onClose={() => setEditingBooking(null)}
          t={t}
        />
      )}
      
      {viewingWorkout && (
        <WorkoutViewModal
          booking={viewingWorkout.booking}
          workout={viewingWorkout.workout}
          onClose={() => setViewingWorkout(null)}
          t={t}
        />
      )}
      
      {/* Full-Screen Workout Editor (replaces modal) */}
      {editingWorkout && (
        <FullScreenWorkoutEditor
          booking={editingWorkout.booking}
          workout={editingWorkout.workout}
          mode={editingWorkout.mode}
          exercises={exercises}
          templates={templates}
          onSave={handleSaveWorkout}
          onClose={() => setEditingWorkout(null)}
          onFinishWorkout={() => {
            setEditingWorkout(null);
            fetchWorkouts();
            onRefresh();
          }}
          clientId={editingWorkout.booking?.client_id}
          t={t}
        />
      )}
      
      {savingTemplate && (
        <SaveTemplateModal
          booking={savingTemplate.booking}
          workout={savingTemplate.workout}
          onSave={handleSaveTemplateConfirm}
          onClose={() => setSavingTemplate(null)}
        />
      )}
    </div>
  );
};

export default UnifiedBookingsTab;
